"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { ChevronDown, CircleHelp, Plus, User } from "lucide-react";
import Logo from "@/components/brand/Logo";
import NotificationList from "@/components/notifications/NotificationList";
import {
  AuthenticatedGlobalMenuItems,
  GlobalAccountMenuItems,
  GlobalMenuAction,
  GlobalMenuLink,
  PublicGlobalMenuItems,
  getAccountInitials,
} from "@/components/navbar/GlobalNavMenus";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNotificationContext } from "@/context/NotificationContext";
import { useAuth } from "@/context/providers/AuthProvider";
import {
  clearAgentSessionReset,
  clearStoredAgentSessionId,
} from "@/hooks/create-page/utils/agentSessionId";
import {
  redirectToLogin,
  redirectToLoginWithDestination,
} from "@/hooks/useRedirectToLogin";
import type { CreditsResponse } from "@/hooks/useCredits";
import {
  getAccountEmail,
  needsAnonymousWorkHandoff,
} from "@/lib/auth/accountStatus";
import { FormPersistenceService } from "@/services/formPersistenceService";
import { useCreatePageStore } from "@/stores/createPageStore";
import type { TicketNotification } from "@/types/ticketNotifications";
import { trackNavigationClick } from "@/utils/analytics";
import {
  CREATE_SIGN_IN_HANDOFF_ERROR,
  prepareCreateSignInHandoff,
} from "@/utils/createSignInHandoff";

function useSafeNotificationContext() {
  try {
    return useNotificationContext();
  } catch {
    return {
      unreadCount: 0,
      refreshNotifications: async () => {},
    };
  }
}

interface NavbarProps {
  /** Kept for shell compatibility; balances now come from CreditsProvider. */
  initialCredits?: CreditsResponse;
  /** Retained for route-shell compatibility; the shell owns visibility. */
  compactUntilLg?: boolean;
  /** Create owns the reset confirmation and decides when a new dress is safe. */
  onNewDress?: () => void;
  newDressDisabled?: boolean;
  onDesignHelp?: () => void;
}

function AccountMenuButton({
  loading,
  user,
  onSignIn,
  onSignOut,
}: {
  loading: boolean;
  user: {
    email?: string | null;
    email_confirmed_at?: string | null;
    is_anonymous?: boolean;
    new_email?: string | null;
    user_metadata?: Record<string, unknown>;
  } | null;
  onSignIn: () => void;
  onSignOut: () => void;
}) {
  if (loading) {
    return (
      <div
        data-slot="navbar-auth-pending"
        aria-hidden="true"
        className="size-11 animate-pulse rounded-full bg-muted/40"
      />
    );
  }

  const initials = user ? getAccountInitials(user) : null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={user ? "Open profile menu" : "Open account menu"}
          title={user ? "Profile" : "Account"}
          className="size-11 shrink-0 rounded-full border-border bg-card p-0 font-semibold shadow-none data-[state=open]:bg-accent"
        >
          {initials ? (
            <span aria-hidden>{initials}</span>
          ) : (
            <User className="size-4" aria-hidden />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-64 max-w-[calc(100vw-2rem)] rounded-xl border-border p-1.5 shadow-md"
      >
        <GlobalAccountMenuItems
          user={user}
          onSignIn={onSignIn}
          onSignOut={onSignOut}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Navbar({
  compactUntilLg = false,
  onNewDress,
  newDressDisabled = false,
  onDesignHelp,
}: NavbarProps = {}) {
  const pathname = usePathname() || "/";
  const router = useRouter();
  const { user, roles, loading: authLoading, isAnonymous } = useAuth();
  const { unreadCount, refreshNotifications } = useSafeNotificationContext();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const hasAccount = Boolean(user) && !isAnonymous;
  const accountUser =
    hasAccount && user
      ? { ...user, email: getAccountEmail(user) || undefined }
      : null;
  const visibleNotificationCount = hasAccount ? unreadCount : 0;
  const isCreateRoute =
    pathname === "/create" || pathname.startsWith("/create/");

  const handleSignIn = () => {
    if (authLoading) return;

    if (isCreateRoute && (!user || needsAnonymousWorkHandoff(user))) {
      const handoff = prepareCreateSignInHandoff({
        state: useCreatePageStore.getState(),
        currentHref: window.location.href,
        sourceUserId: user?.id,
      });
      if (!handoff) {
        useCreatePageStore
          .getState()
          .setAgentError(CREATE_SIGN_IN_HANDOFF_ERROR);
        return;
      }
      redirectToLoginWithDestination(router, handoff.returnPath);
      return;
    }

    redirectToLogin(router, "/account?tab=profile");
  };

  const handleSignOut = () => {
    if (!hasAccount) return;
    FormPersistenceService.clearStateForSignOut();
    clearStoredAgentSessionId();
    clearAgentSessionReset();
    window.location.assign("/api/auth/signout");
  };

  const handleOpenNotifications = () => {
    setNotificationsOpen(true);
    void refreshNotifications();
  };

  const handleNotificationClick = (notification: TicketNotification) => {
    setNotificationsOpen(false);
    router.push(`/ticket/${notification.ticket_id}`);
  };

  return (
    <>
      <header
        className="app-navbar sticky top-0 z-40 h-[calc(var(--navbar-height-mobile)+env(safe-area-inset-top))] shrink-0 pt-[env(safe-area-inset-top)] lg:h-[calc(var(--navbar-height-desktop)+env(safe-area-inset-top))]"
        data-compact-until-lg={compactUntilLg || undefined}
      >
        <div className="mx-auto flex h-16 w-full max-w-screen-2xl items-center justify-between gap-4 px-4 sm:px-6 lg:h-[var(--navbar-height-desktop)] lg:px-8">
          <Link
            href="/"
            aria-label="Build-a-Dress home"
            className="inline-flex min-h-11 min-w-0 items-center rounded-lg pr-2 transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring sm:pr-10"
            onClick={() =>
              trackNavigationClick({
                destination: "/",
                source: "navbar_logo",
                navigation_type: "internal",
              })
            }
          >
            <Logo
              variant="flat"
              size="sm"
              priority
              ariaLabel="Build-a-Dress"
              className="max-h-full w-auto max-w-[min(160px,100%)] object-contain sm:max-w-[min(250px,100%)]"
            />
          </Link>

          <div className="flex min-w-0 shrink-0 items-center justify-end gap-2">
            {!isCreateRoute ? (
              <Button
                asChild
                variant="primary"
                className="hidden min-h-11 rounded-xl px-4 shadow-none lg:inline-flex"
              >
                <Link
                  href="/create"
                  onClick={() =>
                    trackNavigationClick({
                      destination: "/create",
                      source: "navbar_primary_action",
                      navigation_type: "internal",
                    })
                  }
                >
                  <Plus className="size-4" aria-hidden />
                  Create a dress
                </Link>
              </Button>
            ) : onNewDress ? (
              <Button
                type="button"
                variant="primary"
                disabled={newDressDisabled}
                onClick={onNewDress}
                className="hidden min-h-11 rounded-xl px-4 shadow-none lg:inline-flex"
              >
                <Plus className="size-4" aria-hidden />
                New dress
              </Button>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="xl"
                  aria-label={
                    visibleNotificationCount > 0
                      ? `Menu, ${visibleNotificationCount} unread notifications`
                      : "Menu"
                  }
                  className="relative h-11 rounded-xl px-3 shadow-none data-[state=open]:bg-accent data-[state=open]:text-accent-foreground has-[>svg]:px-3"
                >
                  <span>Menu</span>
                  <ChevronDown
                    className="size-4 text-muted-foreground"
                    aria-hidden
                  />
                  {visibleNotificationCount > 0 ? (
                    <span
                      aria-hidden
                      className="absolute -right-1 -top-1 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none tabular-nums text-destructive-foreground"
                    >
                      {visibleNotificationCount > 99
                        ? "99+"
                        : visibleNotificationCount}
                    </span>
                  ) : null}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={8}
                className="w-72 max-w-[calc(100vw-2rem)] rounded-xl border-border p-1.5 shadow-md"
              >
                {isCreateRoute && onNewDress ? (
                  <>
                    <GlobalMenuAction
                      icon={<Plus aria-hidden />}
                      onSelect={onNewDress}
                      disabled={newDressDisabled}
                      className="lg:hidden"
                    >
                      New dress
                    </GlobalMenuAction>
                    <DropdownMenuSeparator className="lg:hidden" />
                  </>
                ) : null}
                {!isCreateRoute ? (
                  <>
                    <GlobalMenuLink
                      href="/create"
                      icon={<Plus aria-hidden />}
                      className="lg:hidden"
                    >
                      Create a dress
                    </GlobalMenuLink>
                    <DropdownMenuSeparator className="lg:hidden" />
                  </>
                ) : null}
                {isCreateRoute && onDesignHelp ? (
                  <GlobalMenuAction
                    icon={<CircleHelp aria-hidden />}
                    onSelect={onDesignHelp}
                  >
                    Help & Q&A
                  </GlobalMenuAction>
                ) : null}
                {hasAccount ? (
                  <AuthenticatedGlobalMenuItems
                    showRequests={Boolean(roles.admin || roles.manufacturer)}
                    showAdmin={Boolean(roles.admin)}
                    notificationCount={unreadCount}
                    onOpenNotifications={handleOpenNotifications}
                  />
                ) : (
                  <PublicGlobalMenuItems />
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <AccountMenuButton
              loading={authLoading}
              user={accountUser}
              onSignIn={handleSignIn}
              onSignOut={handleSignOut}
            />
          </div>
        </div>
      </header>

      <Dialog open={notificationsOpen} onOpenChange={setNotificationsOpen}>
        <DialogContent title={null} className="gap-3 p-4 sm:max-w-lg">
          <DialogHeader className="pr-12">
            <DialogTitle>Notifications</DialogTitle>
            <DialogDescription className="sr-only">
              Review updates and open the related dress request.
            </DialogDescription>
          </DialogHeader>
          <NotificationList
            showHeader={false}
            maxHeight="min(65dvh, 480px)"
            className="shadow-none"
            onNotificationClick={handleNotificationClick}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
